import { useMemo } from 'react';
import { useAdvancedSearchRequestBuilder } from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import { useComparisonWeekIds } from 'src/components/BeaconV2Components/SplineChartV2/useComparisonWeekIds';
import { useAppSelector } from 'src/utils/Hooks';
import { useBrandsFollowingIds } from 'src/utils/Hooks/reduxSelectors';
import useGenericAdvancedSearch from 'src/utils/Hooks/useGenericAdvancedSearch';

export const useAtlasV2BrandSplineChartData = () => {
  const indexName = 'atlas-sales';

  const categoryIds = useAppSelector((state) => state.entityService.mainEntity.categoryIds);
  const retailerId = useAppSelector((state) => state.retailer.id);
  const { startWeek: currentStartWeekId, endWeek: currentEndWeekId } = useAppSelector((state) => state.mainTimePeriod);
  const brandIds = useBrandsFollowingIds();

  // Use expanded comparison period for YTD + Prior Year (fetches full prior year)
  const { comparisonStartWeekId, comparisonEndWeekId } = useComparisonWeekIds();

  const requestIdMain = 'currentAllBrandsByWeekId';
  const brandsByWeekIdRequestBuilderMain = useAdvancedSearchRequestBuilder(requestIdMain, indexName);

  const brandsByWeekIdRequestPrimary = useMemo(() => {
    const brandsByWeekIdAggregationBuilder = new AggregationBuilder('brandId,weekId');

    brandsByWeekIdAggregationBuilder.addAggregationField('retailSales', 'retailSales', 'sum', true);
    brandsByWeekIdAggregationBuilder.addConditionTermFilter('retailerId', [retailerId]);
    brandsByWeekIdAggregationBuilder.addConditionRangeFilter('weekId', currentStartWeekId, currentEndWeekId);

    brandsByWeekIdRequestBuilderMain
      .setPageNumber(1)
      .setPageSize(10000)
      .setPeriod('year')
      .setDoAggregation(true)
      .setReturnDocuments(false)
      .addConditionTermFilter('categoryId', 'should', categoryIds)
      .addConditionTermFilter('brandId', 'should', brandIds)
      .setSearchBy('parent')
      .addAggregation(brandsByWeekIdAggregationBuilder.build())
      .addSortField('retailSales', 'retailSales', 'sum', true);

    return brandsByWeekIdRequestBuilderMain.build();
  }, [retailerId, currentStartWeekId, currentEndWeekId, brandsByWeekIdRequestBuilderMain, categoryIds, brandIds]);

  const requestIdComparison = 'currentAllBrandsByWeekId';
  const brandsByWeekIdRequestBuilderComparison = useAdvancedSearchRequestBuilder(requestIdComparison, indexName);

  const brandsByWeekIdRequestSecondary = useMemo(() => {
    const brandsByWeekIdAggregationBuilder = new AggregationBuilder('brandId,weekId');

    brandsByWeekIdAggregationBuilder.addAggregationField('retailSales', 'retailSales', 'sum', true);
    brandsByWeekIdAggregationBuilder.addConditionTermFilter('retailerId', [retailerId]);
    brandsByWeekIdAggregationBuilder.addConditionRangeFilter('weekId', comparisonStartWeekId, comparisonEndWeekId);

    brandsByWeekIdRequestBuilderComparison
      .setPageNumber(1)
      .setPageSize(10000)
      .setPeriod('year')
      .setDoAggregation(true)
      .setReturnDocuments(false)
      .addConditionTermFilter('categoryId', 'should', categoryIds)
      .addConditionTermFilter('brandId', 'should', brandIds)
      .setSearchBy('parent')
      .addAggregation(brandsByWeekIdAggregationBuilder.build())
      .addSortField('retailSales', 'retailSales', 'sum', true);

    return brandsByWeekIdRequestBuilderComparison.build();
  }, [
    retailerId,
    comparisonStartWeekId,
    comparisonEndWeekId,
    brandsByWeekIdRequestBuilderComparison,
    categoryIds,
    brandIds
  ]);

  const { data: splineChartData, isLoading: isSpineChartLoading } = useGenericAdvancedSearch({
    requestId: requestIdMain,
    queryKeys: [brandsByWeekIdRequestPrimary, brandsByWeekIdRequestSecondary],
    requestBody: [brandsByWeekIdRequestPrimary, brandsByWeekIdRequestSecondary]
  });

  const primaryData = useMemo(() => {
    if (splineChartData) {
      const { data } = splineChartData;

      const currentData = [data[0]];
      const byBrandId = currentData[0].aggregations.by_brandId;

      const formattedData = {};

      byBrandId.forEach((field) => {
        const { brandId } = field.additionalMetaData;
        const { weekId } = field.additionalMetaData;
        const currentValue = field.value;

        if (!formattedData[brandId]) {
          formattedData[brandId] = { brandId, weeklyData: [] };
        }

        formattedData[brandId].weeklyData.push({ weekId, currentValue });
      });

      const result = Object.values(formattedData);

      return { groupedCurrentData: result };
    }
    return null;
  }, [splineChartData]);

  const secondaryData = useMemo(() => {
    if (splineChartData) {
      const { data } = splineChartData;

      const currentData = data[1];
      const byBrandId = currentData.aggregations.by_brandId;

      const formattedData = {};

      byBrandId.forEach((field) => {
        const { brandId } = field.additionalMetaData;
        const { weekId } = field.additionalMetaData;
        const currentValue = field.value;

        if (!formattedData[brandId]) {
          formattedData[brandId] = { brandId, weeklyData: [] };
        }

        // Keep original weekId - SplineChartV2 handles alignment via categories
        formattedData[brandId].weeklyData.push({ weekId, currentValue });
      });

      const result = Object.values(formattedData);
      const sortedResult = result.map((brand) => {
        return {
          brandId: brand.brandId,
          weeklyData: brand.weeklyData.sort((a, b) => b.weekId - a.weekId)
        };
      });
      return { groupedCurrentData: sortedResult };
    }
    return null;
  }, [splineChartData]);

  return {
    primaryData,
    secondaryData,
    isSpineChartLoading
  };
};
